import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Checkbox,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFileExplorerContext } from '../../FileExplorerContext';
import { FolderRow } from './FolderRow';
import { getFolderLink } from '../../../../utils/getFolderName';
import { FileRow } from './FileRow';
import { actionsFor, canCheckFile } from '../../modules/FileActionsMenu';

/** "2026-08-11" → the user's locale date, like every other date in the app. */
const formatGroupDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

/**
 * TableView - Table-based file/folder listing
 *
 * Features:
 * - Folder rows (clickable to navigate)
 * - File rows with metadata and an optional "⋯" actions menu
 * - Optional checkboxes for acting on several files at once
 * - Date categorization (optional)
 * - Selection highlighting
 */
export const TableView = ({
  folders = [],
  files = [],
  categorizeByDate = false,
  expandedCategories = new Set(),
  onExpandCategory,
  onFolderClick,
  onFileClick,
  onFileAction,
  fileActions,
  folderActions,
  onFolderAction,
  onRename,
  parentFolderName,
  excludedFileIds = [],
  getDragData,
  isOver,
  canDrop = false,
  checkable = false,
  checkedIds = [],
  onToggleCheck,
  onToggleCheckAll,
  emptyState,
}) => {
  const { t } = useTranslation(['files', 'common']);
  const { selectedFiles, isFileSelected, handleFileSelect, selectionMode } = useFileExplorerContext();

  const handleFolderRowClick = (folder) => {
    if (onFolderClick) {
      onFolderClick(folder);
    }
  };

  const handleFileRowClick = (file, event) => {
    // Don't trigger row click if clicking on action buttons
    if (event?.target?.closest?.('button')) {
      return;
    }

    // Use context's selection handler
    handleFileSelect(file);

    // Also notify parent
    if (onFileClick) {
      onFileClick(file);
    }
  };

  const toggleCategory = (date) => {
    if (onExpandCategory) {
      onExpandCategory(date);
    }
  };

  // Determine if we're using categorized format
  const isCategorized = categorizeByDate && files.length > 0 && files[0]?.date;
  // Groups without files (e.g. future occurrences of a recurring task) are just noise
  const categories = isCategorized ? files.filter(category => category.count > 0) : [];
  const hasActions = typeof fileActions === 'function' || fileActions?.length > 0 || Boolean(folderActions);
  const columnCount = 6 + (checkable ? 1 : 0) + (hasActions ? 1 : 0);
  const checkableFiles = isCategorized
    ? []
    : files.filter(f => !excludedFileIds.includes(f.file_id) && canCheckFile(fileActions, f));
  const allChecked = checkableFiles.length > 0 && checkableFiles.every(f => checkedIds.includes(f.file_id));
  const someChecked = checkableFiles.some(f => checkedIds.includes(f.file_id));

  const renderFile = (file) => (
    <FileRow
      key={file.file_id}
      file={file}
      selected={selectionMode === 'compare' ? selectedFiles[0]?.file_id === file.file_id : isFileSelected(file)}
      secondarySelected={selectionMode === 'compare' && selectedFiles[1]?.file_id === file.file_id}
      onClick={handleFileRowClick}
      onFileAction={onFileAction}
      actions={actionsFor(fileActions, file)}
      hasActions={hasActions}
      onRename={onRename && ((file, name) => onRename('file', file, name))}
      disabled={excludedFileIds.includes(file.file_id)}
      dragData={getDragData?.(file, 'file')}
      checkable={checkable}
      canCheck={canCheckFile(fileActions, file)}
      checked={checkedIds.includes(file.file_id)}
      onToggleCheck={onToggleCheck}
    />
  );

  const isEmpty = folders.length === 0 && (isCategorized ? categories.length === 0 : files.length === 0);

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}>
      <Table sx={{ '& .MuiTableCell-root': { py: 1, px: 1.5 } }}>
        <TableHead>
          <TableRow>
            {checkable && (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  disabled={checkableFiles.length === 0}
                  onChange={() => onToggleCheckAll?.(allChecked ? [] : checkableFiles)}
                  inputProps={{ 'aria-label': t('files:select_all') }}
                />
              </TableCell>
            )}
            <TableCell width="50px"></TableCell>
            <TableCell>{t('files:file_name')}</TableCell>
            <TableCell>{t('files:file_type')}</TableCell>
            <TableCell>{t('files:file_size')}</TableCell>
            <TableCell>{t('files:uploaded_by')}</TableCell>
            <TableCell>{t('files:uploaded_at')}</TableCell>
            {hasActions && <TableCell width="48px" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Folders */}
          {folders.map((folder) => (
            <FolderRow
              key={folder.folder_id}
              folder={folder}
              link={getFolderLink(folder, parentFolderName)}
              selected={false}
              onClick={handleFolderRowClick}
              dragData={getDragData?.(folder, 'folder')}
              isOver={isOver?.(`folder:${folder.folder_id}`)}
              canDrop={canDrop}
              checkable={checkable}
              hasActions={hasActions}
              actions={folderActions?.(folder)}
              onAction={onFolderAction}
              onRename={onRename && ((item, name) => onRename('folder', item, name))}
            />
          ))}

          {/* Files - Categorized or Flat */}
          {isCategorized ? (
            categories.map((category) => {
              const expanded = expandedCategories.has(category.date);
              const Chevron = expanded ? ExpandMoreIcon : ChevronRightIcon;
              return (
                <React.Fragment key={category.date}>
                  <TableRow>
                    <TableCell
                      onClick={() => toggleCategory(category.date)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleCategory(category.date);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={expanded}
                      colSpan={columnCount}
                      sx={{
                        fontWeight: 600,
                        py: 1,
                        userSelect: 'none',
                        cursor: 'pointer',
                        bgcolor: 'action.hover',
                        '&:hover': { backgroundColor: 'action.selected' },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chevron fontSize="small" />
                        {formatGroupDate(category.date)}
                        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                          · {t('files:files_count', { count: category.count })}
                        </Box>
                        {category.highlight && (
                          <Chip label={t('files:labels.this_task')} color="primary" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {expanded && category.attachments.map(renderFile)}
                </React.Fragment>
              );
            })
          ) : (
            files.map(renderFile)
          )}

          {/* Empty state */}
          {isEmpty && (
            <TableRow>
              <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                {emptyState || (
                  <Box sx={{ color: 'text.secondary' }}>
                    {t('files:empty_folder')}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
